import {
  Button,
  Input,
  Textarea,
  VStack,
  Text,
  createListCollection,
  Flex,
  Portal,
  Select,
  Dialog,
  CloseButton,
} from '@chakra-ui/react';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import { useEffect, useState, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useWhatsAppChannel } from '@/hooks/WAHA/useWhatsapp';
import { useWhatsappContacts } from '@/hooks/WAHA/useWhatsappContacts';
import { useUsers } from '@/hooks/useUsers';
import { whatsappApiClient } from '@/services/api-client';
import { createMessage } from '@/features/messages/messages.service';
import { useCreateConversation } from '@/features/conversations/conversations.hook';
import { persistStorageService } from '@/features/auth/persist-storage';
import { User } from '@/features/users/users.types';
import { OrgLocation } from '@/features/organizations/organizations.types';
import { useQueryClient } from '@tanstack/react-query';
import { Channel } from '@/features/channels/channels.types';
import { Chatbot } from '@/features/chatbots/chatbots.types';
import {
  ConversationData,
  ConversationFormData,
  conversationSchema,
  DefaultConversationFormData,
} from '../conversations.types';
import { LoaderOverlay } from '@/components/loader-overlay';
import { useNavigate } from 'react-router-dom';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConversationCreated: (conversationId: string) => void;
  defaultData?: DefaultConversationFormData;
}

export default function NewWhatsAppConversationModal({
  isOpen,
  onClose,
  onConversationCreated,
  defaultData,
}: Props) {
  const { useWhatsAppChannels, useChatbots } = useWhatsAppChannel();

  const { data = [], isLoading: isLoadingChannels } = useWhatsAppChannels();
  const { data: chatbots = [], isLoading: isLoadingChatbots } = useChatbots();
  const channels = data?.filter((channel) => channel.status === 'WORKING');

  // TODO: Need to investigate if need that
  if (isLoadingChannels || isLoadingChatbots) {
    return null;
    // return <LoaderOverlay isLoading={true} />;
  }

  if (defaultData && chatbots && channels) {
    return (
      <CreateAutomatedConversation
        defaultData={defaultData}
        onConversationCreatedCallback={onConversationCreated}
      />
    );
  }

  return (
    <NewWhatsAppConversationModalForm
      channels={channels}
      chatbots={chatbots}
      isOpen={isOpen}
      onClose={onClose}
      onConversationCreated={onConversationCreated}
      defaultData={defaultData}
    />
  );
}

function NewWhatsAppConversationModalForm({
  channels,
  chatbots,
  isOpen,
  onClose,
  onConversationCreated,
  defaultData,
}: {
  channels: Channel[];
  chatbots: Chatbot[];
  defaultData?: DefaultConversationFormData;
} & Props) {
  // State for tracking auto-populated user
  const [isUserAutoPopulated, setIsUserAutoPopulated] = useState(false);
  const [isCreatingConversationLoading, setIsCreatingConversationLoading] =
    useState(false);

  const { mutateAsync: createConversation } = useCreateConversation();
  const { getUserByPhone, createUser } = useUsers();
  const queryClient = useQueryClient();

  // Form setup
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ConversationFormData>({
    resolver: zodResolver(conversationSchema),
    defaultValues: {
      ...defaultData,
      channelId: channels?.[0]?.id || '',
      chatbotId: chatbots?.length === 1 ? chatbots?.[0]?.id : '',
      phoneNumber: defaultData?.phoneNumber || '',
      userFirstName: defaultData?.userFirstName || '',
      userLastName: defaultData?.userLastName || '',
      userMember: defaultData?.userMember ?? undefined,
      initialMessage: '',
    },
  });

  const selectedChannelId = watch('channelId');
  const phoneNumber = watch('phoneNumber');

  // Get the selected channel to access its WAHA session from config
  const selectedChannel = channels?.find((c) => c.id === selectedChannelId);
  const wahaSessionId =
    selectedChannel?.channel_config?.sessionId || 'default';

  // Get contact validation
  const { checkNumberStatus } = useWhatsappContacts(wahaSessionId);

  // Debounced user lookup function
  const debouncedUserLookup = useCallback(
    async (phone: string) => {
      if (!phone || phone.length < 8) {
        setIsUserAutoPopulated(false);
        return;
      }

      try {
        const formattedPhone = formatPhoneForWhatsApp(phone);
        const existingUser = await getUserByPhone(formattedPhone);

        if (existingUser) {
          setValue('userFirstName', existingUser.first_name || '');
          setValue('userLastName', existingUser.last_name || '');
          setValue('userMember', existingUser.isMember ?? undefined);
          setIsUserAutoPopulated(true);

          toaster.create({
            title: 'User Found',
            description:
              `Found existing user: ${existingUser.first_name || ''} ${existingUser.last_name || ''}`.trim(),
            type: 'success',
            duration: 2000,
          });
        } else {
          setIsUserAutoPopulated(false);
        }
      } catch (error) {
        console.error('Error looking up user by phone:', error);
        setIsUserAutoPopulated(false);
      }
    },
    [getUserByPhone, setValue],
  );

  // Reset form when modal opens/closes
  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  // Watch phone number changes with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      debouncedUserLookup(phoneNumber);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [phoneNumber, debouncedUserLookup]);

  // Utility functions
  const formatPhoneForWhatsApp = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    return digits;
  };

  const getCurrentUser = () => {
    return persistStorageService.getPersistItem<User>({
      name: 'user',
      parse: true,
    });
  };

  const getOrgAndLocation = () => {
    const selectedOrg = persistStorageService.getPersistItem<OrgLocation>({
      name: 'selectedOrganisation',
      parse: true,
    });

    const selectedLoc = persistStorageService.getPersistItem<OrgLocation>({
      name: 'selectedLocation',
      parse: true,
    });

    return { selectedOrg, selectedLoc };
  };

  const sendDirectMessage = async ({
    conversationId,
    channelId,
    wahaSession,
    content,
    chatId,
    user,
  }: {
    conversationId: string;
    channelId: string;
    wahaSession: string;
    content: string;
    chatId: string;
    user?: { id: string; firstName: string; lastName: string };
  }) => {
    try {
      const { selectedOrg, selectedLoc } = getOrgAndLocation();
      const currentUser = getCurrentUser();

      await whatsappApiClient.post(`/api/sendText`, {
        session: wahaSession,
        chatId: chatId,
        text: content,
      });

      // Create message in database
      const messageData = {
        conversation_id: conversationId,
        channel_id: channelId,
        content: content,
        from_agent: false,
        sender_id: currentUser?.id || '',
        sender_name: currentUser?.first_name
          ? `${currentUser?.first_name} ${currentUser?.last_name}`
          : 'Agent',
        receiver_id: user?.id || '',
        receiver_name: user?.firstName
          ? `${user.firstName} ${user.lastName || ''}`.trim()
          : 'Customer',
        message_type: 'text',
        is_read: true,
        read_at: new Date().toISOString(),
        organisation_id: selectedOrg?.id || '',
        location_id: selectedLoc?.id || '',
      };

      return await createMessage(messageData);
    } catch (error) {
      console.error('Error sending direct message:', error);
      toaster.create({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        type: 'error',
        duration: 5000,
      });
      throw error;
    }
  };

  const handleUserManagement = async ({
    formattedPhone,
    firstName,
    lastName,
  }: {
    formattedPhone: string;
    firstName?: string;
    lastName?: string;
  }) => {
    const existingUser = await getUserByPhone(formattedPhone);
    const { selectedOrg, selectedLoc } = getOrgAndLocation();

    if (existingUser) {
      return {
        id: existingUser.id,
        firstName: existingUser.first_name || '',
        lastName: existingUser.last_name || '',
        isMember: existingUser.isMember || false,
      };
    }

    if (firstName && lastName) {
      const newUser = await createUser({
        first_name: firstName,
        last_name: lastName || '',
        phone_number: formattedPhone,
        email: `${formattedPhone.replace('+', '')}@placeholder.com`,
        organisation_id: selectedOrg?.id || '',
        location_id: selectedLoc?.id || '',
      });

      return {
        id: newUser.id,
        firstName: firstName,
        lastName: lastName || '',
        organisation_id: selectedOrg?.id || '',
        location_id: selectedLoc?.id || '',
      };
    }

    return null;
  };

  const onSubmit = async (data: ConversationFormData) => {
    try {
      setIsCreatingConversationLoading(true);
      const formattedPhone = formatPhoneForWhatsApp(data.phoneNumber);

      // Check WhatsApp number validity
      const numberCheckResult = await checkNumberStatus({
        phone: formattedPhone,
      });

      if (!numberCheckResult.numberExists) {
        toaster.create({
          title: 'Invalid Number',
          description: 'This phone number is not registered on WhatsApp',
          type: 'error',
          duration: 3000,
        });
        return;
      }

      // Handle user management
      const user = await handleUserManagement({
        formattedPhone,
        firstName: data.userFirstName,
        lastName: data.userLastName,
      });

      // Create conversation
      const chatId =
        numberCheckResult.chatId || `${formattedPhone.replace('+', '')}@c.us`;

      const conversationData: ConversationData = {
        channel_id: data.channelId,
        phone_number: formattedPhone,
        summary: `WhatsApp Chat (${formattedPhone})`,
        has_agent_active: false,
        wab_chatId_external: chatId,
        ...(data.chatbotId && { chatbot_id: data.chatbotId }),
        ...(user?.id && { user_id: user.id }),
      };

      const newConversation = await createConversation(conversationData);

      if (!newConversation?.data?.id) {
        throw new Error('Failed to create conversation');
      }

      // Send initial message if provided
      if (data.initialMessage) {
        try {
          const conversationId =
            newConversation?.data?.conversation_id || newConversation?.data?.id;
          await sendDirectMessage({
            conversationId,
            channelId: data.channelId,
            wahaSession: wahaSessionId,
            content: data.initialMessage,
            chatId,
            user: user || undefined,
          });
          // TODO invalidate message thread here
          queryClient.invalidateQueries({
            queryKey: ['messages', 'infinite', conversationId],
          });
        } catch (messageError) {
          console.error('Error sending message:', messageError);
          toaster.create({
            title: 'Warning',
            description:
              "Conversation created but couldn't send the initial message.",
            type: 'warning',
            duration: 3000,
          });
        }
      }

      // Success
      onConversationCreated(
        newConversation?.data?.conversation_id || newConversation?.data?.id,
      );
      toaster.create({
        title: 'Success',
        description: 'Conversation created successfully',
        type: 'success',
        duration: 3000,
      });
      onClose();
    } catch (error) {
      console.error('Error creating conversation:', error);
      toaster.create({
        title: 'Error',
        description: 'Failed to create conversation. Please try again.',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsCreatingConversationLoading(false);
    }
  };

  // Create collections for selects
  const channelsCollection = createListCollection({
    items: channels
      ?.filter((channel) => channel.status === 'WORKING')
      .map((channel) => ({
        label: channel.name,
        value: channel.id,
      })),
  });

  const chatbotsCollection = createListCollection({
    items: chatbots.map((chatbot) => ({
      label: chatbot.name,
      value: chatbot.id,
    })),
  });

  return (
    <Dialog.Root
      open={isOpen}
      size="lg"
      closeOnInteractOutside={false}
      lazyMount
      unmountOnExit
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content style={{ zIndex: 9999 }}>
            <Dialog.Header>
              <Dialog.Title fontSize="lg" fontWeight="bold">
                New WhatsApp Conversation
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" onClick={onClose} />
            </Dialog.CloseTrigger>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Dialog.Body>
                <VStack gap={4} align="stretch">
                  {/* Channel Selection */}
                  <Field
                    label="WhatsApp Channel"
                    invalid={!!errors.channelId}
                    errorText={errors.channelId?.message}
                  >
                    <Controller
                      name="channelId"
                      control={control}
                      render={({ field }) => (
                        <Select.Root
                          value={field.value ? [field.value] : []}
                          onValueChange={(e) =>
                            field.onChange(e.value[0] || '')
                          }
                          collection={channelsCollection}
                        >
                          <Select.HiddenSelect />
                          <Select.Control>
                            <Select.Trigger>
                              <Select.ValueText placeholder="Select a channel" />
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                              <Select.Indicator />
                            </Select.IndicatorGroup>
                          </Select.Control>
                          <Portal>
                            <Select.Positioner style={{ zIndex: 10000 }}>
                              <Select.Content style={{ zIndex: 10000 }}>
                                {channelsCollection.items.map((channel) => (
                                  <Select.Item
                                    item={channel}
                                    key={channel.value}
                                  >
                                    {channel.label}
                                    <Select.ItemIndicator />
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Portal>
                        </Select.Root>
                      )}
                    />

                    {channels.length === 0 && (
                      <Text fontSize="sm" color="red.500">
                        No WhatsApp channels available. Please create a channel
                        first.
                      </Text>
                    )}
                  </Field>

                  {/* Chatbot Selection */}
                  <Field label="Chatbot (optional)">
                    <Controller
                      name="chatbotId"
                      control={control}
                      render={({ field }) => (
                        <Select.Root
                          value={field.value ? [field.value] : []}
                          onValueChange={(e) =>
                            field.onChange(e.value[0] || '')
                          }
                          collection={chatbotsCollection}
                        >
                          <Select.HiddenSelect />
                          <Select.Control>
                            <Select.Trigger>
                              <Select.ValueText placeholder="No chatbot" />
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                              <Select.Indicator />
                            </Select.IndicatorGroup>
                          </Select.Control>
                          <Portal>
                            <Select.Positioner style={{ zIndex: 10000 }}>
                              <Select.Content style={{ zIndex: 10000 }}>
                                {chatbotsCollection.items.map((chatbot) => (
                                  <Select.Item
                                    item={chatbot}
                                    key={chatbot.value}
                                  >
                                    {chatbot.label}
                                    <Select.ItemIndicator />
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Portal>
                        </Select.Root>
                      )}
                    />

                    <Text fontSize="sm" color="gray.500">
                      Select a chatbot to handle this conversation automatically
                    </Text>
                  </Field>

                  {/* Phone Number */}
                  <Field
                    label="Phone Number"
                    invalid={!!errors.phoneNumber}
                    errorText={errors.phoneNumber?.message}
                  >
                    <Controller
                      name="phoneNumber"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} placeholder="31612345678" />
                      )}
                    />
                    <Text fontSize="sm" color="gray.500">
                      Enter the full phone number with country code
                    </Text>
                  </Field>

                  {/* User Information Fields */}
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.700"
                    mt={2}
                  >
                    Contact Information (optional)
                    {isUserAutoPopulated && (
                      <Text
                        as="span"
                        fontSize="xs"
                        color="green.600"
                        fontWeight="normal"
                        ml={2}
                      >
                        • Auto-populated from existing user
                      </Text>
                    )}
                  </Text>

                  <Field label="First Name">
                    <Controller
                      name="userFirstName"
                      control={control}
                      disabled={isUserAutoPopulated}
                      render={({ field }) => (
                        <Input {...field} placeholder="Contact's first name" />
                      )}
                    />
                  </Field>

                  <Field label="Last Name">
                    <Controller
                      name="userLastName"
                      control={control}
                      disabled={isUserAutoPopulated}
                      render={({ field }) => (
                        <Input {...field} placeholder="Contact's last name" />
                      )}
                    />
                  </Field>

                  {/* Initial Message */}
                  <Field label="Initial Message (optional)">
                    <Controller
                      name="initialMessage"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          placeholder="Type your first message..."
                          minHeight="80px"
                        />
                      )}
                    />
                  </Field>
                </VStack>
              </Dialog.Body>

              <Dialog.Footer>
                <Flex gap={3} justify="flex-end" width="100%">
                  <Dialog.ActionTrigger asChild>
                    <Button variant="ghost" onClick={onClose}>
                      Cancel
                    </Button>
                  </Dialog.ActionTrigger>
                  <Button
                    type="submit"
                    bg="black"
                    color="white"
                    _hover={{ bg: 'gray.800' }}
                    _active={{ bg: 'gray.900' }}
                    loading={isCreatingConversationLoading}
                    disabled={channels.length === 0}
                  >
                    Create Conversation
                  </Button>
                </Flex>
              </Dialog.Footer>
            </form>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

const CreateAutomatedConversation = ({
  defaultData,
  onConversationCreatedCallback,
}: {
  defaultData: DefaultConversationFormData;
  onConversationCreatedCallback: (conversationId: string) => void;
}) => {
  const { onCreateNewConversation } = useCreateWhatsappNewConversation({
    defaultData,
    onConversationCreated: onConversationCreatedCallback,
  });

  useEffect(() => {
    onCreateNewConversation();
  }, []);
  return null;
};

export const useCreateWhatsappNewConversation = ({
  onConversationCreated,
  defaultData,
}: Pick<Props, 'onConversationCreated' | 'defaultData'>) => {
  const { useWhatsAppChannels, useChatbots } = useWhatsAppChannel();
  const { data = [], isLoading: isLoadingChannels } = useWhatsAppChannels();
  const { data: chatbots = [] } = useChatbots();
  const [isConversationFlowLoading, setIsConversationFlowLoading] =
    useState(true);
  const channels = data?.filter((channel) => channel.status === 'WORKING');

  const { mutateAsync: createConversation } = useCreateConversation();
  const { getUserByPhone, createUser } = useUsers();

  // Form setup
  const { watch, setValue } = useForm<ConversationFormData>({
    resolver: zodResolver(conversationSchema),
    defaultValues: {
      ...defaultData,
      channelId: channels?.[0]?.id || '',
      chatbotId: chatbots?.length === 1 ? chatbots?.[0]?.id : '',
      phoneNumber: defaultData?.phoneNumber || '',
      userFirstName: defaultData?.userFirstName || '',
      userLastName: defaultData?.userLastName || '',
      userMember: defaultData?.userMember ?? undefined,
      initialMessage: '',
    },
  });

  const selectedChannelId = watch('channelId');
  const phoneNumber = watch('phoneNumber');

  // Get the selected channel to access its WAHA session from config
  const selectedChannel = channels?.find((c) => c.id === selectedChannelId);
  const wahaSessionId =
    selectedChannel?.channel_config?.sessionId || 'default';

  // Get contact validation
  const { checkNumberStatus } = useWhatsappContacts(wahaSessionId);

  // Debounced user lookup function
  const debouncedUserLookup = useCallback(
    async (phone: string) => {
      if (!phone || phone.length < 8) {
        return;
      }

      try {
        const formattedPhone = formatPhoneForWhatsApp(phone);
        const existingUser = await getUserByPhone(formattedPhone);

        if (existingUser) {
          setValue('userFirstName', existingUser.first_name || '');
          setValue('userLastName', existingUser.last_name || '');
          setValue('userMember', existingUser.isMember ?? undefined);
        }
      } catch (error) {
        console.error('Error looking up user by phone:', error);
      }
    },
    [getUserByPhone, setValue],
  );

  // Watch phone number changes with debounce
  useEffect(() => {
    if (channels.length > 0 && !isLoadingChannels) {
      debouncedUserLookup(phoneNumber);
    }
  }, [phoneNumber, channels, isLoadingChannels]);

  // Utility functions
  const formatPhoneForWhatsApp = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    return digits;
  };

  const getOrgAndLocation = () => {
    const selectedOrg = persistStorageService.getPersistItem<OrgLocation>({
      name: 'selectedOrganisation',
      parse: true,
    });

    const selectedLoc = persistStorageService.getPersistItem<OrgLocation>({
      name: 'selectedLocation',
      parse: true,
    });

    return { selectedOrg, selectedLoc };
  };

  const handleUserManagement = async ({
    formattedPhone,
    firstName,
    lastName,
  }: {
    formattedPhone: string;
    firstName?: string;
    lastName?: string;
  }) => {
    const existingUser = await getUserByPhone(formattedPhone);
    const { selectedOrg, selectedLoc } = getOrgAndLocation();

    if (existingUser) {
      return {
        id: existingUser.id,
        firstName: existingUser.first_name || '',
        lastName: existingUser.last_name || '',
        isMember: existingUser.isMember || false,
      };
    }

    if (firstName && lastName) {
      const newUser = await createUser({
        first_name: firstName,
        last_name: lastName || '',
        phone_number: formattedPhone,
        email: `${formattedPhone.replace('+', '')}@placeholder.com`,
        organisation_id: selectedOrg?.id || '',
        location_id: selectedLoc?.id || '',
      });

      return {
        id: newUser.id,
        firstName: firstName,
        lastName: lastName || '',
        organisation_id: selectedOrg?.id || '',
        location_id: selectedLoc?.id || '',
      };
    }

    return null;
  };

  const onSubmit = async (data: ConversationFormData) => {
    try {
      const formattedPhone = formatPhoneForWhatsApp(data.phoneNumber);

      // Check WhatsApp number validity
      const numberCheckResult = await checkNumberStatus({
        phone: formattedPhone,
      });

      if (!numberCheckResult.numberExists) {
        toaster.create({
          title: 'Invalid Number',
          description: 'This phone number is not registered on WhatsApp',
          type: 'error',
          duration: 3000,
        });
        return;
      }

      // Handle user management
      const user = await handleUserManagement({
        formattedPhone,
        firstName: data.userFirstName,
        lastName: data.userLastName,
      });

      // Create conversation
      const chatId =
        numberCheckResult.chatId || `${formattedPhone.replace('+', '')}@c.us`;

      const conversationData: ConversationData = {
        channel_id: data.channelId,
        phone_number: formattedPhone,
        summary: `WhatsApp Chat (${formattedPhone})`,
        has_agent_active: false,
        wab_chatId_external: chatId,
        ...(data.chatbotId && { chatbot_id: data.chatbotId }),
        ...(user?.id && { user_id: user.id }),
      };

      const newConversation = await createConversation(conversationData);
      console.log(
        '[CreateWhatsappNewConversation] newConversation',
        newConversation,
      );

      if (newConversation?.data?.conversation_id) {
        console.log(
          '[CreateWhatsappNewConversation] setting selected conversation id',
          newConversation.data.conversation_id,
        );
        setIsConversationFlowLoading(false);
        onConversationCreated(newConversation.data.conversation_id);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      toaster.create({
        title: 'Error',
        description: 'Failed to create conversation. Please try again.',
        type: 'error',
        duration: 5000,
      });
    }
  };

  const dataForForm = watch();

  const onCreateNewConversation = useCallback(() => {
    if (dataForForm?.channelId) {
      onSubmit(dataForForm);
    }
  }, [dataForForm?.channelId]);

  return {
    onCreateNewConversation,
    isConversationFlowLoading,
  };
};
